<template>
  <div class="adlm" :class="{ 'adlm--selecting': selectionMode }" dir="rtl">
    <div class="adlm__header">
      <div class="adlm__bar">
        <div class="adlm__bar-actions">
          <button
            type="button"
            class="adlm__bar-btn adlm__bar-btn--create"
            :aria-label="$t('newDocument')"
            @click="list.goTo('accounting')"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            type="button"
            class="adlm__bar-btn"
            :aria-label="$t('refresh')"
            @click="onRefreshTap"
          >
            <v-icon size="20" color="#37474F">mdi-refresh</v-icon>
          </button>
        </div>
        <h1 class="adlm__title">{{ $t($route.name) }}</h1>
        <button
          type="button"
          class="adlm__bar-btn adlm__bar-btn--back"
          :aria-label="$t('back')"
          @click="goBack"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>

      <div class="adlm__search-row">
        <div class="adlm__search">
          <v-icon size="20" color="#90A4AE" class="adlm__search-icon">
            mdi-magnify
          </v-icon>
          <input
            v-model="list.search"
            type="search"
            class="adlm__search-input"
            :placeholder="$t('search')"
            autocomplete="off"
          />
          <button
            v-if="list.search"
            type="button"
            class="adlm__search-clear"
            :aria-label="$t('clear')"
            @click="list.search = ''"
          >
            <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
          </button>
        </div>
        <button
          type="button"
          class="adlm__filter-btn"
          :class="{ 'adlm__filter-btn--on': hasAnyFilter }"
          :aria-label="$t('filter')"
          @click="filterSheet = true"
        >
          <v-icon size="22" :color="hasAnyFilter ? '#1565C0' : '#546E7A'">
            mdi-filter-outline
          </v-icon>
          <span v-if="hasAnyFilter" class="adlm__filter-dot" />
        </button>
        <button
          type="button"
          class="adlm__filter-btn"
          :class="{ 'adlm__filter-btn--on': selectionMode }"
          :aria-label="$t('select')"
          @click="toggleSelectionMode"
        >
          <v-icon size="22" :color="selectionMode ? '#1565C0' : '#546E7A'">
            mdi-checkbox-multiple-marked-outline
          </v-icon>
        </button>
      </div>

      <div v-if="selectionMode" class="adlm__select-bar" dir="rtl">
        <button type="button" class="adlm__select-bar-btn" @click="selectAllLoaded">
          <v-icon size="17" color="#5B7C99">mdi-check-all</v-icon>
          {{ $t("selectAllItems") }}
        </button>
        <span class="adlm__select-count">
          {{ selectedItems.length }} {{ $t("selectedCount") }}
        </span>
        <button type="button" class="adlm__select-bar-btn" @click="clearSelection">
          <v-icon size="17" color="#90A4AE">mdi-close</v-icon>
          {{ $t("deselectAll") }}
        </button>
      </div>
    </div>

    <div v-if="list.loading && !displayItems.length" class="adlm__state">
      <v-progress-circular indeterminate color="#5B7C99" size="28" />
    </div>
    <div v-else-if="!displayItems.length" class="adlm__state adlm__state--empty">
      <img
        class="adlm__empty-logo"
        src="@/assets/dashboard/no-data-cloud.png"
        alt=""
      />
      <p class="adlm__empty-text">{{ $t("no-data") }}</p>
    </div>
    <div v-else class="adlm__list">
      <div class="adlm__count">
        {{ $numberFormat(list.totalItems || 0) }} {{ $t("accDocListDocuments") }}
      </div>
      <article
        v-for="(item, index) in displayItems"
        :key="item.id || index"
        class="adlm-card"
        :class="{ 'adlm-card--selected': selectionMode && isSelected(item) }"
      >
        <div
          v-if="selectionMode"
          class="adlm-card__select-overlay"
          :class="{ 'adlm-card__select-overlay--on': isSelected(item) }"
          @click="toggleSelect(item)"
        >
          <span v-if="isSelected(item)" class="adlm-card__check">
            <v-icon size="17" color="#FFFFFF">mdi-check</v-icon>
          </span>
        </div>
        <div class="adlm-card__head">
          <div class="adlm-card__title-block">
            <button
              type="button"
              class="adlm-card__name-link"
              :disabled="!!item.lock"
              @click="list.edit(item)"
            >
              <span class="adlm-card__name">
                {{ $t("document") }} {{ item.number }}
              </span>
              <v-icon v-if="item.lock" size="14" color="#E53935" class="ms-1">
                mdi-lock-outline
              </v-icon>
              <v-icon v-else size="12" color="#879EB6">mdi-pencil-outline</v-icon>
            </button>
            <div class="adlm-card__sub adlm-card__sub--clamp">
              {{ item.content || "—" }}
            </div>
          </div>
          <div class="adlm-card__head-side">
            <span
              class="adlm-doc-pill"
              :class="item.doc_type == 'always' ? 'adlm-doc-pill--always' : ''"
            >
              {{ item.doc_type == "always" ? $t("always") : $t("temporary") }}
            </span>
            <span class="adlm-card__number">#{{ item.id }}</span>
          </div>
        </div>

        <div class="adlm-card__amounts">
          <div class="adlm-card__amount-row">
            <span class="adlm-card__amount-label">{{ $t("date") }}</span>
            <span class="adlm-card__amount-value">{{ item.date || "—" }}</span>
          </div>
          <div class="adlm-card__amount-row">
            <span class="adlm-card__amount-label">{{ $t("documentType") }}</span>
            <span class="adlm-card__amount-value">
              {{ $t(item.type) }} · {{ wayLabel(item) }}
            </span>
          </div>
          <div class="adlm-card__amount-row">
            <span class="adlm-card__amount-label">{{ $t("debtor") }}</span>
            <span class="adlm-card__amount-value adlm-amount">
              {{ $numberFormat(item.price ? item.price.sum_debtor : 0, 10) }}
            </span>
          </div>
          <div class="adlm-card__amount-row">
            <span class="adlm-card__amount-label">{{ $t("creditor") }}</span>
            <span class="adlm-card__amount-value adlm-amount">
              {{ $numberFormat(item.price ? item.price.sum_creditor : 0, 10) }}
            </span>
          </div>
          <div v-if="hasLastStatus(item)" class="adlm-card__amount-row">
            <span class="adlm-card__amount-label">{{ $t("status") }}</span>
            <span class="adlm-card__status-pill" :style="lastStatusStyle(item)">
              {{ item.last_status.status.name }}
            </span>
          </div>
        </div>

        <div v-if="!selectionMode" class="adlm-card__footer">
          <div class="adlm-card__actions">
            <button
              v-if="list.canRemoveDoc(item)"
              type="button"
              class="adlm-btn adlm-btn--danger"
              :disabled="!!item.lock"
              @click="askDelete(item)"
            >
              <v-icon size="14" color="#E53935">mdi-trash-can-outline</v-icon>
              {{ $t("remove") }}
            </button>
            <button
              type="button"
              class="adlm-btn adlm-btn--primary"
              :disabled="!!item.lock"
              @click="list.edit(item)"
            >
              <v-icon size="14" color="#FFFFFF">mdi-pencil-outline</v-icon>
              {{ $t("edit") }}
            </button>
            <button
              type="button"
              class="adlm-btn adlm-btn--outline"
              @click="openDetails(item)"
            >
              <v-icon size="14" color="#5B7C99">mdi-eye-outline</v-icon>
              {{ $t("details") }}
            </button>
          </div>
          <button
            type="button"
            class="adlm-rel-btn"
            @click="list.openDocView(item)"
          >
            <v-icon size="14" color="#5B7C99">mdi-file-eye-outline</v-icon>
            {{ $t("document_view") }}
          </button>
        </div>
      </article>
      <div v-if="appending" class="adlm__state adlm__state--more">
        <v-progress-circular indeterminate color="#5B7C99" size="22" />
      </div>
    </div>

    <div ref="sentinel" class="adlm__sentinel" />

    <div v-if="selectionMode" class="adlm-bulkbar" dir="rtl">
      <button
        type="button"
        class="adlm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkPrint"
      >
        <v-icon size="19" color="#5B7C99">mdi-printer-check</v-icon>
        {{ $t("print") }}
      </button>
      <button
        v-if="lockPermission"
        type="button"
        class="adlm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('lock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-outline</v-icon>
        {{ $t("lock") }}
      </button>
      <button
        v-if="lockPermission"
        type="button"
        class="adlm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('unlock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-open-outline</v-icon>
        {{ $t("unlock") }}
      </button>
      <button
        type="button"
        class="adlm-bulkbar__btn adlm-bulkbar__btn--danger"
        :disabled="!selectedItems.length"
        @click="askGroupDelete"
      >
        <v-icon size="19" color="#E53935">mdi-trash-can-outline</v-icon>
        {{ $t("groupDelete") }}
      </button>
    </div>

    <!-- Details -->
    <v-bottom-sheet v-model="detailSheet" scrollable>
      <v-card class="adlm-sheet">
        <div class="adlm-sheet__handle" />
        <div class="adlm-sheet__header" dir="rtl">
          <div class="adlm-sheet__header-side">
            <span v-if="detailItem" class="adlm-sheet__num-chip">
              #{{ detailItem.id }}
            </span>
          </div>
          <div class="adlm-sheet__title">{{ $t("details") }}</div>
          <div class="adlm-sheet__header-side adlm-sheet__header-side--end">
            <button
              type="button"
              class="adlm-sheet__back"
              :aria-label="$t('back')"
              @click="detailSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <div v-if="detailItem" class="adlm-sheet__quickbar" dir="rtl">
          <button
            type="button"
            class="adlm-sheet__quick"
            :disabled="!!detailItem.lock"
            @click="runNamedAction('edit')"
          >
            <v-icon size="18" color="#5B7C99">mdi-pencil-outline</v-icon>
            {{ $t("edit") }}
          </button>
          <button type="button" class="adlm-sheet__quick" @click="runNamedAction('view')">
            <v-icon size="18" color="#5B7C99">mdi-file-eye-outline</v-icon>
            {{ $t("document_view") }}
          </button>
          <button type="button" class="adlm-sheet__quick" @click="runNamedAction('print')">
            <v-icon size="18" color="#5B7C99">mdi-printer-outline</v-icon>
            {{ $t("print") }}
          </button>
          <button type="button" class="adlm-sheet__quick" @click="moreSheet = true">
            <v-icon size="18" color="#5B7C99">mdi-dots-horizontal</v-icon>
            {{ $t("more") }}
          </button>
        </div>

        <div v-if="detailItem" class="adlm-sheet__body" dir="rtl">
          <div class="adlm-sum" :class="{ 'adlm-sum--warn': detailDiff(detailItem) }">
            <div class="adlm-sum__col">
              <span>{{ $t("debtor") }}</span>
              <strong>
                {{ $numberFormat(detailItem.price ? detailItem.price.sum_debtor : 0, 10) }}
              </strong>
            </div>
            <div class="adlm-sum__col">
              <span>{{ $t("creditor") }}</span>
              <strong>
                {{ $numberFormat(detailItem.price ? detailItem.price.sum_creditor : 0, 10) }}
              </strong>
            </div>
            <div class="adlm-sum__col">
              <span>{{ $t("difference") }}</span>
              <strong>{{ $numberFormat(detailDiff(detailItem), 10) }}</strong>
            </div>
          </div>
          <div class="adlm-sheet__card">
            <div
              v-for="(row, i) in detailRows(detailItem)"
              :key="'detail-row-' + i"
              class="adlm-sheet__row"
              :class="{ 'adlm-sheet__row--band': row.kind === 'band' }"
            >
              <span class="adlm-sheet__row-label">{{ row.label }}</span>
              <span class="adlm-sheet__row-value">{{ row.value }}</span>
            </div>
            <div class="adlm-sheet__row">
              <span class="adlm-sheet__row-label">{{ $t("status") }}</span>
              <span
                v-if="hasLastStatus(detailItem)"
                class="adlm-card__status-pill"
                :style="lastStatusStyle(detailItem)"
              >
                {{ detailItem.last_status.status.name }}
              </span>
              <span v-else class="adlm-sheet__row-value">—</span>
            </div>
            <div class="adlm-sheet__row">
              <span class="adlm-sheet__row-label">{{ $t("tag") }}</span>
              <span
                v-if="detailItem.tags && detailItem.tags.length"
                class="adlm-sheet__tags"
              >
                <v-chip
                  v-for="(tag, ti) in detailItem.tags"
                  :key="'detail-tag-' + ti"
                  :color="tag.color || '#607D8B'"
                  label
                  small
                  class="px-1"
                >
                  <span class="px-1 white--text">{{ tag.text }}</span>
                </v-chip>
              </span>
              <span v-else class="adlm-sheet__row-value">—</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="moreSheet">
      <v-card class="adlm-sheet">
        <div class="adlm-sheet__handle" />
        <div class="adlm-sheet__header" dir="rtl">
          <span class="adlm-sheet__header-spacer" />
          <div class="adlm-sheet__title">{{ $t("more") }}</div>
          <button
            type="button"
            class="adlm-sheet__back"
            :aria-label="$t('back')"
            @click="moreSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div v-if="detailItem" class="adlm-sheet__body" dir="rtl">
          <div class="adlm-sheet__actions adlm-sheet__actions--sheet">
            <button
              v-if="lockPermission"
              type="button"
              class="adlm-sheet__action"
              @click="runNamedAction('lock')"
            >
              <v-icon size="20" :color="detailItem.lock ? 'green' : 'pink'">
                {{ detailItem.lock ? "mdi-lock-open-outline" : "mdi-lock-outline" }}
              </v-icon>
              <span>{{ detailItem.lock ? $t("unlock") : $t("lock") }}</span>
            </button>
            <button type="button" class="adlm-sheet__action" @click="runNamedAction('copy')">
              <v-icon size="20" color="#5B7C99">mdi-content-copy</v-icon>
              <span>{{ $t("copy") }}</span>
            </button>
            <button
              type="button"
              class="adlm-sheet__action"
              v-clipboard:copy="list.docLink(detailItem)"
              @click="onCopyLinkQuick"
            >
              <v-icon size="20" color="#5B7C99">mdi-link</v-icon>
              <span>{{ $t("copy-link") }}</span>
            </button>
            <button
              v-if="$store.state.admin == 'active'"
              type="button"
              class="adlm-sheet__action"
              @click="runNamedAction('toTemp')"
            >
              <v-icon size="20" color="#5B7C99">mdi-undo-variant</v-icon>
              <span>{{ $t("Convert-permanent-doc-to-temporary") }}</span>
            </button>
            <div class="adlm-sheet__action adlm-sheet__action--log">
              <log
                :title-prop="$t('history')"
                icon-prop="mdi-history"
                :listProp="true"
                :type-prop="list.typeForm"
                :refIdProp="detailItem.id"
                mode-prop="button"
              />
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Layered filter -->
    <v-bottom-sheet v-model="filterSheet" scrollable>
      <v-card class="adlm-sheet adlm-sheet--filter">
        <div class="adlm-sheet__handle" />
        <div class="adlm-sheet__header" dir="rtl">
          <span class="adlm-sheet__header-spacer" />
          <div class="adlm-sheet__title">{{ $t("filter") }}</div>
          <button
            type="button"
            class="adlm-sheet__back"
            :aria-label="$t('back')"
            @click="filterSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="adlm-filter-nav" dir="rtl">
          <button
            v-for="group in filterGroups"
            :key="group.key"
            type="button"
            class="adlm-filter-nav__item"
            @click="openFilterGroup(group.key)"
          >
            <v-icon class="adlm-filter-nav__icon" size="22" color="#5B7C99">
              {{ group.icon }}
            </v-icon>
            <span class="adlm-filter-nav__texts">
              <span class="adlm-filter-nav__item-title">{{ group.title }}</span>
              <span class="adlm-filter-nav__item-sub">{{ group.sub }}</span>
            </span>
            <span v-if="groupHasValue(group.key)" class="adlm-filter-nav__dot" />
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div class="adlm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="adlm-sheet__btn adlm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="adlm-sheet__btn adlm-sheet__btn--ghost"
            @click="clearFilter"
          >
            <v-icon size="16" color="#5B7C99">mdi-filter-remove-outline</v-icon>
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="filterGroupSheet" scrollable>
      <v-card class="adlm-sheet adlm-sheet--filter">
        <div class="adlm-sheet__handle" />
        <div class="adlm-sheet__header" dir="rtl">
          <span class="adlm-sheet__header-spacer" />
          <div class="adlm-sheet__title">{{ activeFilterGroupTitle }}</div>
          <button
            type="button"
            class="adlm-sheet__back"
            :aria-label="$t('back')"
            @click="backToFilterMain"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="adlm-filter-fields" dir="rtl">
          <template v-if="filterGroup === 'accounts'">
            <div v-for="p in accountPickers" :key="p.ref" class="adlm-f-field-wrap">
              <div class="adlm-f-field-wrap__label">{{ p.label }}</div>
              <button
                type="button"
                class="adlm-f-picker-field"
                @click="openPicker(p.ref)"
              >
                <span class="adlm-f-picker-field__text">
                  {{ pickerSummary(p.ref, list[p.field]) }}
                </span>
                <span
                  v-if="hasValue(list[p.field])"
                  class="adlm-picker-cover__clear"
                  @click.stop="list[p.field] = null"
                >
                  <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="adlm-picker-host" aria-hidden="true">
                <autocomplete
                  :ref="p.ref"
                  v-model="list[p.field]"
                  :title="p.title"
                  :type="p.type"
                  :filter="p.filter"
                  :multiple="p.multiple"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'range'">
            <div class="adlm-f-pair">
              <div v-for="field in dateFields" :key="field.key" class="adlm-f-field-wrap">
                <div class="adlm-f-field-wrap__label">{{ field.label }}</div>
                <button
                  type="button"
                  class="adlm-f-picker-field"
                  @click="openDateLayer(field)"
                >
                  <span class="adlm-f-picker-field__text adlm-ltr">
                    {{ hasValue(list[field.key]) ? list[field.key] : "—" }}
                  </span>
                  <span
                    v-if="hasValue(list[field.key])"
                    class="adlm-picker-cover__clear"
                    @click.stop="list[field.key] = null"
                  >
                    <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                  </span>
                  <v-icon size="20" color="#78909C">
                    {{ field.type === "time" ? "mdi-clock-outline" : "mdi-calendar" }}
                  </v-icon>
                </button>
              </div>
            </div>
            <div class="adlm-f-pair">
              <div v-for="field in inputFields" :key="field.key" class="adlm-f-field-wrap">
                <div class="adlm-f-field-wrap__label">{{ field.label }}</div>
                <input
                  v-model="list[field.key]"
                  type="text"
                  inputmode="numeric"
                  class="adlm-f-input"
                  placeholder="—"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'category'">
            <div class="adlm-f-field-wrap">
              <div class="adlm-f-field-wrap__label">{{ $t("natureAccount") }}</div>
              <button
                type="button"
                class="adlm-f-picker-field"
                @click="openSelectLayer('type', $t('natureAccount'), list.typeItems || [], 'text', true)"
              >
                <span class="adlm-f-picker-field__text">
                  {{ selectSummary("type", list.typeItems || [], "text") }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
            <div class="adlm-f-field-wrap">
              <div class="adlm-f-field-wrap__label">{{ $t("submitDocWay") }}</div>
              <button
                type="button"
                class="adlm-f-picker-field"
                @click="openSelectLayer('submitDocWay', $t('submitDocWay'), list.submitDocWays, 'label', true)"
              >
                <span class="adlm-f-picker-field__text">
                  {{ selectSummary("submitDocWay", list.submitDocWays, "label") }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
            <div class="adlm-f-field-wrap">
              <div class="adlm-f-field-wrap__label">{{ $t("lockStatusDocuments") }}</div>
              <button
                type="button"
                class="adlm-f-picker-field"
                @click="openSelectLayer('lockStatus', $t('lockStatusDocuments'), list.lockStatusItems || [], 'text', false)"
              >
                <span class="adlm-f-picker-field__text">
                  {{ selectSummary("lockStatus", list.lockStatusItems || [], "text") }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
            <div class="adlm-f-field-wrap">
              <div class="adlm-f-field-wrap__label">{{ $t("tag") }}</div>
              <button type="button" class="adlm-f-picker-field" @click="openPicker('adTag')">
                <span class="adlm-f-picker-field__text">
                  {{ pickerSummary("adTag", list.tag) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="adlm-picker-host" aria-hidden="true">
                <tag ref="adTag" v-model="list.tag" />
              </div>
            </div>
            <div class="adlm-f-field-wrap">
              <div class="adlm-f-field-wrap__label">{{ $t("status") }}</div>
              <div class="adlm-picker-overlay">
                <status
                  ref="adStatus"
                  class="adlm-f-field"
                  v-model="list.sellStatus"
                  :hideconfig="true"
                />
                <button
                  type="button"
                  class="adlm-f-picker-field adlm-picker-cover"
                  @click="openStatusPicker"
                >
                  <span class="adlm-f-picker-field__text">{{ statusSummary() }}</span>
                  <span
                    v-if="hasValue(list.sellStatus)"
                    class="adlm-picker-cover__clear"
                    @click.stop="list.sellStatus = null"
                  >
                    <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                  </span>
                  <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
                </button>
              </div>
            </div>
          </template>
        </div>

        <div class="adlm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="adlm-sheet__btn adlm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="adlm-sheet__btn adlm-sheet__btn--ghost"
            @click="backToFilterMain"
          >
            {{ $t("back") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="dateLayerOpen">
      <v-card class="adlm-sheet adlm-sheet--filter">
        <div class="adlm-sheet__handle" />
        <div class="adlm-sheet__header" dir="rtl">
          <span class="adlm-sheet__header-spacer" />
          <div class="adlm-sheet__title">{{ dateLayer.label }}</div>
          <button
            type="button"
            class="adlm-sheet__back"
            :aria-label="$t('back')"
            @click="dateLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="adlm-mobile-date" dir="rtl">
          <div class="adlm-mobile-date__selected">
            <span class="adlm-mobile-date__label">{{ dateLayer.label }}</span>
            <strong class="adlm-mobile-date__value">
              {{ hasValue(list[dateLayer.key]) ? list[dateLayer.key] : "—" }}
            </strong>
            <button
              v-if="hasValue(list[dateLayer.key])"
              type="button"
              class="adlm-mobile-date__clear"
              @click="list[dateLayer.key] = null"
            >
              {{ $t("clear") }}
            </button>
          </div>
          <date-picker
            v-if="dateLayer.key && dateLayer.type === 'time'"
            :key="dateLayer.key"
            v-model="list[dateLayer.key]"
            inline
            simple
            auto-submit
            type="time"
            :locale="$store.state.date_locale"
            :format="'HH:mm'"
            :display-format="'HH'"
            class="adlm-mobile-date__picker"
            color="#7B8BA3"
          />
          <date-picker
            v-else-if="dateLayer.key"
            :key="dateLayer.key"
            v-model="list[dateLayer.key]"
            inline
            simple
            auto-submit
            :locale="$store.state.date_locale"
            :format="$store.state.date_format"
            class="adlm-mobile-date__picker"
            color="#7B8BA3"
          />
        </div>

        <div class="adlm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="adlm-sheet__btn adlm-sheet__btn--primary"
            @click="dateLayerOpen = false"
          >
            {{ $t("Save") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="selectLayerOpen">
      <v-card class="adlm-sheet adlm-sheet--filter">
        <div class="adlm-sheet__handle" />
        <div class="adlm-sheet__header" dir="rtl">
          <span class="adlm-sheet__header-spacer" />
          <div class="adlm-sheet__title">{{ selectLayerTitle }}</div>
          <button
            type="button"
            class="adlm-sheet__back"
            :aria-label="$t('back')"
            @click="selectLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="adlm-select-layer" dir="rtl">
          <button
            v-for="item in selectLayerItems"
            :key="item.value"
            type="button"
            class="adlm-select-layer__item"
            @click="selectLayerPick(item.value)"
          >
            <span class="adlm-select-layer__text">{{ item[selectLayerTextKey] }}</span>
            <v-icon v-if="isSelectLayerSelected(item.value)" size="20" color="#5B7C99">
              mdi-check
            </v-icon>
          </button>
          <button
            v-if="selectLayerField && hasValue(list[selectLayerField])"
            type="button"
            class="adlm-select-layer__clear"
            @click="selectLayerClear"
          >
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="deleteSheet">
      <v-card class="adlm-confirm-sheet" dir="rtl">
        <div class="adlm-confirm-sheet__handle" />
        <div class="adlm-confirm-sheet__icon">
          <v-icon size="26" color="#E53935">mdi-trash-can-outline</v-icon>
        </div>
        <div class="adlm-confirm-sheet__title">
          {{ deleteGroupMode ? $t("groupDelete") : $t("delete") }}
        </div>
        <p class="adlm-confirm-sheet__subtitle">
          {{ $t("are-you-sure-delete-this-case") }}
        </p>
        <div v-if="deleteGroupMode" class="adlm-confirm-sheet__rows">
          <div class="adlm-confirm-sheet__row">
            <span>{{ $t("totalCount") }}</span>
            <strong>{{ selectedItems.length }}</strong>
          </div>
          <div class="adlm-confirm-sheet__items">
            <div
              v-for="(sel, si) in selectedItems"
              :key="'del-item-' + si"
              class="adlm-confirm-sheet__item"
            >
              <span class="adlm-confirm-sheet__item-main">
                <span class="adlm-confirm-sheet__item-index">{{ si + 1 }}</span>
                <span class="adlm-confirm-sheet__item-name">
                  {{ $t("document") }} {{ sel.number }}
                </span>
              </span>
              <span class="adlm-confirm-sheet__item-number">#{{ sel.id }}</span>
            </div>
          </div>
        </div>
        <div v-else-if="list.deleteItem" class="adlm-confirm-sheet__rows">
          <div class="adlm-confirm-sheet__row">
            <span>{{ $t("document-number") }}</span>
            <strong>{{ list.deleteItem.number }}</strong>
          </div>
          <div class="adlm-confirm-sheet__row">
            <span>{{ $t("date") }}</span>
            <strong>{{ list.deleteItem.date || "—" }}</strong>
          </div>
          <div class="adlm-confirm-sheet__row">
            <span>{{ $t("id") }}</span>
            <strong>{{ list.deleteItem.id }}</strong>
          </div>
        </div>
        <div class="adlm-confirm-sheet__actions">
          <v-btn
            color="error"
            block
            depressed
            height="48"
            class="adlm-confirm-sheet__yes"
            @click="confirmDelete"
          >
            {{ $t("yes") }}
          </v-btn>
          <v-btn
            outlined
            block
            height="48"
            color="secondary"
            class="adlm-confirm-sheet__no"
            @click="deleteSheet = false"
          >
            {{ $t("no") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
export default {
  name: "AccountingDocsListMobile",
  inject: ["accountingDocsList"],
  components: {
    autocomplete: () => import("../../../../components/general/patial/autocomplete"),
    Tag: () => import("../../../../components/general/Tag/tag"),
    Status: () => import("../../../../components/general/Status/status"),
  },
  data() {
    return {
      displayItems: [],
      appending: false,
      observer: null,
      detailSheet: false,
      moreSheet: false,
      detailItem: null,
      filterSheet: false,
      filterGroupSheet: false,
      filterGroup: null,
      dateLayerOpen: false,
      dateLayer: { key: null, label: "", type: "date" },
      selectLayerOpen: false,
      selectLayerField: null,
      selectLayerTitle: "",
      selectLayerItems: [],
      selectLayerTextKey: "text",
      selectLayerMulti: false,
      selectionMode: false,
      selectedItems: [],
      deleteSheet: false,
      deleteGroupMode: false,
    };
  },
  computed: {
    list() {
      return this.accountingDocsList;
    },
    currentPage() {
      return (this.list.tableOptions && this.list.tableOptions.page) || 1;
    },
    lockPermission() {
      return !!this.list.lockPermission;
    },
    accountPickers() {
      return [
        { ref: "adGiven", field: "given", title: "given", type: "given", label: this.$t("given") },
        { ref: "adFin4", field: "fin4", title: "detail", type: "detail", label: this.$t("detail"), multiple: true },
        { ref: "adFin5", field: "fin5", title: "fin5", type: "detail", label: this.$t("fin5") },
        { ref: "adFin6", field: "fin6", title: "fin6", type: "detail", label: this.$t("fin6"), multiple: true },
        { ref: "adBranch", field: "branch", title: "branch", type: "branch", label: this.$t("branch"), filter: true, multiple: true },
        { ref: "adUser", field: "registerar", title: this.$t("registerar"), type: "partners", label: this.$t("registerar"), filter: true, multiple: true },
      ];
    },
    dateFields() {
      return [
        { key: "fromDate", label: this.$t("from") + " " + this.$t("date"), type: "date" },
        { key: "toDate", label: this.$t("until") + " " + this.$t("date"), type: "date" },
        { key: "fromTime", label: this.$t("from-hour"), type: "time" },
        { key: "toTime", label: this.$t("to-hour"), type: "time" },
      ];
    },
    inputFields() {
      return [
        { key: "fromNumber", label: this.$t("from") + " " + this.$t("number") },
        { key: "toNumber", label: this.$t("until") + " " + this.$t("number") },
        { key: "fromAmount", label: this.$t("fromAmount") },
        { key: "toAmount", label: this.$t("toAmount") },
      ];
    },
    filterGroups() {
      return [
        {
          key: "accounts",
          icon: "mdi-account-cash-outline",
          title: this.$t("accDocListFilterAccounts"),
          sub: [this.$t("given"), this.$t("detail"), this.$t("branch"), this.$t("registerar")].join(" · "),
        },
        {
          key: "range",
          icon: "mdi-calendar-range",
          title: this.$t("accDocListFilterRange"),
          sub: [this.$t("date"), this.$t("number"), this.$t("amount")].join(" · "),
        },
        {
          key: "category",
          icon: "mdi-shape-outline",
          title: this.$t("accDocListFilterCategory"),
          sub: [this.$t("natureAccount"), this.$t("submitDocWay"), this.$t("tag"), this.$t("status")].join(" · "),
        },
      ];
    },
    activeFilterGroupTitle() {
      const group = this.filterGroups.find((g) => g.key === this.filterGroup);
      return group ? group.title : this.$t("filter");
    },
    hasAnyFilter() {
      return this.filterGroups.some((g) => this.groupHasValue(g.key));
    },
  },
  watch: {
    "accountingDocsList.items"(val) {
      const items = val || [];
      if (this.appending) {
        const seen = {};
        this.displayItems.forEach((i) => {
          seen[i.id] = true;
        });
        this.displayItems = this.displayItems.concat(items.filter((i) => !seen[i.id]));
        this.appending = false;
      } else {
        this.displayItems = items.slice();
      }
    },
    "accountingDocsList.search"() {
      this.appending = false;
      if (this.currentPage !== 1) this.setPage(1);
    },
    deleteSheet(val) {
      if (!val) this.deleteGroupMode = false;
    },
  },
  // This screen has its own sticky bars; hide the app bottom navigation.
  activated() {
    this.$store.commit("SET_BOTTOMNAVIGATE", false);
  },
  deactivated() {
    this.$store.commit("SET_BOTTOMNAVIGATE", true);
  },
  mounted() {
    this.$store.commit("SET_BOTTOMNAVIGATE", false);
    this.displayItems = (this.list.items || []).slice();
    this.list.tableOptions = Object.assign({}, this.list.tableOptions, {
      page: 1,
      itemsPerPage: 10,
    });
    this.$nextTick(() => {
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0] && entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeDestroy() {
    this.$store.commit("SET_BOTTOMNAVIGATE", true);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    setPage(page) {
      this.list.tableOptions = Object.assign({}, this.list.tableOptions, { page });
    },
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ name: "Home" }).catch(() => {});
    },
    onRefreshTap() {
      this.appending = false;
      if (this.currentPage !== 1) this.setPage(1);
      else this.list.getItems();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    openPicker(refName) {
      let comp = this.$refs[refName];
      if (Array.isArray(comp)) comp = comp[0];
      if (comp && typeof comp.openMobileDialog === "function") {
        comp.openMobileDialog();
      }
    },
    openStatusPicker() {
      const comp = this.$refs.adStatus;
      const combo = comp && comp.$refs ? comp.$refs.combo : null;
      if (combo) {
        if (typeof combo.focus === "function") combo.focus();
        combo.isMenuActive = true;
      }
    },
    statusSummary() {
      const v = this.list.sellStatus;
      if (!this.hasValue(v)) return "—";
      if (typeof v === "object" && !Array.isArray(v)) return v.name || v.text || "—";
      const comp = this.$refs.adStatus;
      const items = (comp && comp.items) || [];
      const arr = Array.isArray(v) ? v : [v];
      const names = arr
        .map((id) => items.find((i) => i && i.id === id))
        .filter((f) => f && f.name)
        .map((f) => f.name);
      return names.length ? names.join(" · ") : String(arr.length);
    },
    openSelectLayer(field, title, items, textKey, multi) {
      this.selectLayerField = field;
      this.selectLayerTitle = title;
      this.selectLayerItems = items || [];
      this.selectLayerTextKey = textKey || "text";
      this.selectLayerMulti = !!multi;
      this.selectLayerOpen = true;
    },
    isSelectLayerSelected(value) {
      if (!this.selectLayerField) return false;
      const current = this.list[this.selectLayerField];
      if (this.selectLayerMulti) {
        return Array.isArray(current) && current.indexOf(value) !== -1;
      }
      return current === value;
    },
    selectLayerPick(value) {
      const field = this.selectLayerField;
      if (!field) return;
      if (this.selectLayerMulti) {
        const current = Array.isArray(this.list[field]) ? this.list[field].slice() : [];
        const idx = current.indexOf(value);
        if (idx === -1) current.push(value);
        else current.splice(idx, 1);
        // submitDocWay is an array in list.vue; others reset to null.
        this.list[field] = current.length || field === "submitDocWay" ? current : null;
        return;
      }
      this.list[field] = value;
      this.selectLayerOpen = false;
    },
    selectLayerClear() {
      const field = this.selectLayerField;
      if (field) this.list[field] = field === "submitDocWay" ? [] : null;
      this.selectLayerOpen = false;
    },
    selectSummary(field, items, textKey) {
      const v = this.list[field];
      if (!this.hasValue(v)) return "—";
      const key = textKey || "text";
      const arr = Array.isArray(v) ? v : [v];
      const names = arr
        .map((val) => (items || []).find((i) => i && i.value === val))
        .filter(Boolean)
        .map((f) => f[key]);
      return names.length ? names.join(" · ") : String(arr.length);
    },
    pickerSummary(refName, value) {
      if (!this.hasValue(value)) return "—";
      let comp = this.$refs[refName];
      if (Array.isArray(comp)) comp = comp[0];
      const items = (comp && comp.items) || [];
      const arr = Array.isArray(value) ? value : [value];
      const names = [];
      arr.forEach((v) => {
        if (v && typeof v === "object") {
          if (v.name || v.text) names.push(v.name || v.text);
        } else {
          const found = items.find((i) => i && (i.id === v || i.value === v));
          if (found && (found.name || found.text)) names.push(found.name || found.text);
        }
      });
      return names.length ? names.join(" · ") : String(arr.length);
    },
    openFilterGroup(key) {
      this.filterGroup = key;
      this.filterSheet = false;
      this.$nextTick(() => {
        this.filterGroupSheet = true;
      });
    },
    backToFilterMain() {
      this.filterGroupSheet = false;
      this.$nextTick(() => {
        this.filterSheet = true;
      });
    },
    hasValue(v) {
      if (Array.isArray(v)) return v.length > 0;
      return v !== null && v !== undefined && v !== "";
    },
    groupHasValue(key) {
      const l = this.list;
      if (key === "accounts") {
        return this.accountPickers.some((p) => this.hasValue(l[p.field]));
      }
      if (key === "range") {
        return this.dateFields
          .concat(this.inputFields)
          .some((f) => this.hasValue(l[f.key]) && l[f.key] !== "0" && l[f.key] !== 0);
      }
      if (key === "category") {
        return ["type", "submitDocWay", "lockStatus", "tag", "sellStatus"].some((f) =>
          this.hasValue(l[f])
        );
      }
      return false;
    },
    openDateLayer(field) {
      this.dateLayer = { key: field.key, label: field.label, type: field.type };
      this.dateLayerOpen = true;
    },
    applyFilter() {
      this.filterSheet = false;
      this.filterGroupSheet = false;
      this.selectLayerOpen = false;
      this.dateLayerOpen = false;
      this.appending = false;
      if (this.currentPage !== 1) this.setPage(1);
      else this.list.getItems();
    },
    clearFilter() {
      this.list.resetFilters();
      this.applyFilter();
    },
    loadMore() {
      const l = this.list;
      if (l.loading || this.appending) return;
      const page = this.currentPage;
      if (page >= (l.pageCount || 1)) return;
      this.appending = true;
      this.setPage(page + 1);
    },
    wayLabel(item) {
      return item.ref_type == null ? this.$t("manual") : this.$t(item.ref_type);
    },
    detailDiff(item) {
      const p = item && item.price ? item.price : {};
      return Math.abs((parseFloat(p.sum_debtor) || 0) - (parseFloat(p.sum_creditor) || 0));
    },
    hasLastStatus(item) {
      return !!(item && item.last_status && item.last_status.status && item.last_status.status.name);
    },
    lastStatusStyle(item) {
      if (!this.hasLastStatus(item)) return null;
      const color = item.last_status.status.color || "#5B7C99";
      return { background: color, color: "#FFFFFF", borderColor: color };
    },
    openDetails(item) {
      this.detailItem = item;
      this.moreSheet = false;
      this.detailSheet = true;
    },
    askDelete(item) {
      this.moreSheet = false;
      this.detailSheet = false;
      this.deleteGroupMode = false;
      this.list.selected = [];
      this.list.deleteItem = item;
      this.deleteSheet = true;
    },
    askGroupDelete() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.list.deleteItem = null;
      this.deleteGroupMode = true;
      this.deleteSheet = true;
    },
    confirmDelete() {
      this.deleteSheet = false;
      if (this.deleteGroupMode) {
        this.list.removegroup();
        this.exitSelectionMode(false);
      } else {
        this.list.remove();
      }
    },
    toggleSelectionMode() {
      if (this.selectionMode) this.exitSelectionMode();
      else this.selectionMode = true;
    },
    exitSelectionMode(clearShell = true) {
      this.selectionMode = false;
      this.selectedItems = [];
      if (clearShell) this.list.selected = [];
    },
    isSelected(item) {
      return this.selectedItems.some((i) => i.id === item.id);
    },
    toggleSelect(item) {
      this.selectedItems = this.isSelected(item)
        ? this.selectedItems.filter((i) => i.id !== item.id)
        : this.selectedItems.concat([item]);
    },
    selectAllLoaded() {
      const seen = {};
      this.selectedItems.forEach((i) => {
        seen[i.id] = true;
      });
      this.selectedItems = this.selectedItems.concat(this.displayItems.filter((i) => !seen[i.id]));
    },
    clearSelection() {
      this.selectedItems = [];
    },
    syncSelection() {
      this.list.selected = this.selectedItems.slice();
    },
    bulkLock(type) {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.list.lockGroup(type);
      this.exitSelectionMode(false);
    },
    bulkPrint() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.list.groupPrint();
      this.exitSelectionMode();
    },
    onCopyLinkQuick() {
      this.moreSheet = false;
      this.list.copied();
    },
    runNamedAction(name) {
      const item = this.detailItem;
      if (!item) return;
      this.moreSheet = false;
      this.detailSheet = false;
      if (name === "edit") this.list.edit(item);
      else if (name === "view") this.list.openDocView(item);
      else if (name === "print") this.list.openDialogPrint(item.id);
      else if (name === "lock") this.list.lock(item, item.lock ? "unlock" : "lock");
      else if (name === "copy") this.list.copy(item);
      else if (name === "toTemp") this.list.convertDocToTempConfirmation(item);
    },
    detailRows(item) {
      if (!item) return [];
      const rows = [];
      const push = (label, value, kind) =>
        rows.push({ label, value: value == null || value === "" ? "—" : value, kind: kind || null });
      push(this.$t("document-number"), item.number, "band");
      push(this.$t("turningNumber"), item.turning_number, "band");
      push(this.$t("date"), item.date);
      push(this.$t("documentType"), this.$t(item.type) + " · " + (item.doc_type == "always" ? this.$t("always") : this.$t("temporary")));
      push(this.$t("submitDocWay"), this.wayLabel(item));
      push(this.$t("branch"), item.branch && item.branch.name);
      push(this.$t("content"), item.content);
      const user = item.user && (item.user.name || [item.user.firstname, item.user.lastname].filter(Boolean).join(" "));
      push(this.$t("registerar"), user);
      if (item.time) push(this.$t("time"), item.time);
      return rows;
    },
  },
};
</script>

<style scoped>
.adlm {
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
  background: #ffffff;
  min-height: calc(100vh - 52px);
  box-sizing: border-box;
  padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
}
.adlm--selecting {
  padding-bottom: calc(140px + env(safe-area-inset-bottom, 0px));
}
.adlm__header {
  position: sticky;
  top: 52px;
  z-index: 4;
  background: #ffffff;
  padding: 6px 0 10px;
  border-bottom: 1px solid #eef1f4;
  margin-bottom: 10px;
}
.adlm__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}
.adlm__bar-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 80px;
}
.adlm__bar-btn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.adlm__bar-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.adlm__bar-btn--create {
  background: rgba(91, 124, 153, 0.1);
}
.adlm__bar-btn--back {
  margin-inline-start: auto;
}
.adlm__title {
  flex: 1 1 auto;
  margin: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.adlm__search-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.adlm__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 6px;
  background: #f3f5f7;
}
.adlm__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #37474f;
}
.adlm__search-clear {
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
}
.adlm__filter-btn {
  position: relative;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border: 1px solid #e0e4e8;
}
.adlm__filter-btn:active {
  background: rgba(91, 124, 153, 0.08);
}
.adlm__filter-btn--on {
  border-color: rgba(91, 124, 153, 0.45);
  background: rgba(91, 124, 153, 0.08);
}
.adlm__filter-dot {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.adlm__select-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 4px 0;
}
.adlm__select-bar-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.08);
  font-size: 11px;
  font-weight: 600;
  color: #455a64;
  cursor: pointer;
}
.adlm__select-bar-btn:active {
  background: rgba(91, 124, 153, 0.16);
}
.adlm__select-count {
  flex: 1 1 auto;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.adlm__state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
  color: #607d8b;
  font-size: 13px;
}
.adlm__state--empty {
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(100vh - 220px);
  padding: 32px 16px 24px;
  background: #ffffff;
}
.adlm__state--more {
  padding: 14px 0;
}
.adlm__empty-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.85;
}
.adlm__empty-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
  text-align: center;
}
.adlm__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}
.adlm__sentinel {
  height: 1px;
}
.adlm-card {
  position: relative;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: #f8fafc;
  padding: 12px;
  transition: transform 0.15s ease;
}
.adlm-card--selected {
  transform: scale(0.99);
  border-color: rgba(21, 101, 192, 0.45);
}
.adlm-card__select-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.adlm-card__select-overlay--on {
  background: rgba(38, 50, 56, 0.12);
}
.adlm-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0d47a1;
  box-shadow: 0 2px 6px rgba(13, 71, 161, 0.4);
}
.adlm-bulkbar {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: calc(72px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.14);
}
.adlm-bulkbar__btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
  padding: 6px 2px;
  border: 0;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.adlm-bulkbar__btn:active {
  background: rgba(91, 124, 153, 0.15);
}
.adlm-bulkbar__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.adlm-bulkbar__btn--danger {
  color: #e53935;
  background: rgba(229, 57, 53, 0.06);
}
.adlm-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.adlm-card__title-block {
  min-width: 0;
  flex: 1 1 auto;
}
.adlm-card__name-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.adlm-card__name-link:disabled {
  cursor: default;
}
.adlm-card__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: right;
}
.adlm-card__sub {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.adlm-card__number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 6px;
  padding: 2px 8px;
}
.adlm-card__amounts {
  margin-top: 10px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.adlm-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.adlm-card__amount-label {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.adlm-card__amount-value {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.adlm-card__status-pill {
  display: inline-flex;
  align-items: center;
  max-width: 62%;
  height: 24px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.adlm-card__status-pill--empty {
  background: rgba(144, 164, 174, 0.12);
  color: #90a4ae;
  border-color: transparent;
}
.adlm-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.adlm-card__actions {
  display: flex;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
  flex-wrap: wrap;
}
.adlm-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 34px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 0;
  -webkit-tap-highlight-color: transparent;
}
.adlm-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.adlm-btn--danger {
  border: 1px solid rgba(229, 57, 53, 0.35);
  background: rgba(229, 57, 53, 0.06);
  color: #c62828;
}
.adlm-btn--primary {
  background: #5b7c99;
  color: #ffffff;
}
.adlm-btn--outline {
  background: #ffffff;
  color: #5b7c99;
  border: 1px solid #cfd8dc;
}
.adlm-rel-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.08);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.adlm-rel-btn:active {
  background: rgba(91, 124, 153, 0.16);
}
.adlm-rel-btn:disabled {
  background: transparent;
  color: #90a4ae;
  opacity: 0.55;
  cursor: default;
}
.adlm-rel-empty {
  padding: 18px 0;
  font-size: 12.5px;
  color: #90a4ae;
  text-align: center;
}
.adlm-rel-item {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 10px;
}
.adlm-rel-item__title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
}
.adlm-rel-item__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;
}
.adlm-rel-item__key {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.adlm-rel-item__val {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.adlm-sheet {
  border-radius: 6px 6px 0 0 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.adlm-sheet__handle {
  flex: 0 0 auto;
  width: 44px;
  height: 4px;
  border-radius: 6px;
  background: #cfd8dc;
  margin: 10px auto 2px;
}
.adlm-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 10px;
  border-bottom: 1px solid #eef1f4;
}
.adlm-sheet__header-side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  min-width: 0;
}
.adlm-sheet__header-side--end {
  justify-content: flex-end;
}
.adlm-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.adlm-sheet__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.adlm-sheet__num-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  background: rgba(135, 158, 182, 0.14);
  border: 1px solid rgba(135, 158, 182, 0.28);
  color: #5b7c99;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.adlm-sheet__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.adlm-sheet__back:active {
  background: rgba(91, 124, 153, 0.1);
}
.adlm-sheet__quickbar {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
}
.adlm-sheet__quick {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 46px;
  padding: 4px 2px;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.adlm-sheet__quick:disabled {
  opacity: 0.45;
  cursor: default;
}
.adlm-sheet__quick:active:not(:disabled) {
  background: rgba(91, 124, 153, 0.14);
}
.adlm-sheet__name-block {
  flex: 0 0 auto;
  background: #edf2f7;
  border-radius: 6px;
  padding: 10px 14px;
  margin: 6px 16px 0;
}
.adlm-sheet__name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.adlm-sheet__name-value {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 70%;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adlm-sheet__name-block .adlm-sheet__linkrow {
  margin: 8px 0 0;
  background: rgba(255, 255, 255, 0.75);
}
.adlm-sheet__body {
  overflow-y: auto;
  padding: 8px 16px 20px;
}
.adlm-sheet__card {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  padding: 4px 14px;
}
.adlm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
}
.adlm-sheet__card .adlm-sheet__row:last-child {
  border-bottom: 0;
}
.adlm-sheet__row--band {
  margin: 0 -14px;
  padding: 8px 14px;
  border-bottom-color: rgba(91, 124, 153, 0.12);
  background: rgba(91, 124, 153, 0.05);
}
.adlm-sheet__row-label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.adlm-sheet__row-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.adlm-sheet__tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
  max-width: 62%;
}
.adlm-sheet__linkrow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  margin: 6px 0 2px;
  padding: 0 10px;
  border: 1px solid rgba(0, 150, 136, 0.16);
  border-radius: 6px;
  background: rgba(0, 150, 136, 0.07);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.adlm-sheet__linkrow-text {
  flex: 1 1 auto;
  font-size: 12.5px;
  font-weight: 700;
  color: #00796b;
  text-align: right;
}
.adlm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.adlm-sheet__actions--sheet {
  padding-top: 4px;
}
.adlm-sheet__action {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 42px;
  padding: 0 6px;
  border: 0;
  border-bottom: 1px solid #e8edf2;
  border-radius: 6px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.adlm-sheet__action:disabled {
  opacity: 0.45;
  cursor: default;
}
.adlm-sheet__action:active:not(:disabled) {
  background: rgba(91, 124, 153, 0.08);
}
.adlm-sheet__action--danger {
  color: #c62828;
}
.adlm-sheet__action--log {
  cursor: default;
  padding: 0;
}
.adlm-sheet__action--log > div {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.adlm-sheet__action--log ::v-deep .v-list-item {
  flex: 1 1 auto;
  min-height: 42px;
  height: 42px;
  padding: 0;
}
.adlm-sheet__action--log ::v-deep .v-list-item__icon {
  margin: 0 !important;
  min-width: 19px;
  align-self: center;
}
.adlm-sheet__action--log ::v-deep .v-list-item__icon .v-icon {
  font-size: 19px !important;
  color: #5b7c99 !important;
}
.adlm-sheet__action--log ::v-deep .v-list-item__title {
  margin-inline-start: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
}
.adlm-sheet--filter {
  max-height: 92vh;
  border-radius: 6px 6px 0 0 !important;
}
.adlm-sheet__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef1f4;
  background: #ffffff;
}
.adlm-sheet__btn {
  flex: 1 1 0;
  height: 44px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.adlm-sheet__btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.adlm-sheet__btn--primary:active {
  background: #4d6a84;
}
.adlm-sheet__btn--ghost {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #546e7a;
}
.adlm-sheet__btn--ghost:active {
  background: rgba(91, 124, 153, 0.08);
}
.adlm-filter-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #ffffff;
}
.adlm-filter-nav__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.adlm-filter-nav__item:active {
  background: #f7f9fc;
}
.adlm-filter-nav__item:last-child {
  border-bottom: 0;
}
.adlm-filter-nav__icon {
  flex: 0 0 auto;
}
.adlm-filter-nav__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.adlm-filter-nav__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}
.adlm-filter-nav__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adlm-filter-nav__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.adlm-filter-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px 6px;
  background: #ffffff;
}
.adlm-filter-fields > * {
  margin-bottom: 14px;
}
.adlm-picker-host {
  display: none !important;
}
.adlm-picker-overlay {
  position: relative;
  height: 40px;
  overflow: hidden;
  border-radius: 6px;
}
.adlm-picker-cover {
  position: absolute !important;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
}
.adlm-picker-cover__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}
.adlm-f-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.adlm-f-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 4px;
}
.adlm-f-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.adlm-f-picker-field:active {
  background: #eef4f8;
  border-color: #90caf9;
}
.adlm-f-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adlm-select-layer {
  padding: 6px 16px 20px;
}
.adlm-select-layer__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.adlm-select-layer__text {
  font-size: 13.5px;
  font-weight: 500;
  color: #37474f;
}
.adlm-select-layer__clear {
  display: block;
  width: 100%;
  padding: 14px 4px 4px;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #c62828;
  text-align: center;
  cursor: pointer;
}
.adlm-mobile-date {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
  background: #ffffff;
}
.adlm-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 6px;
  background: #f7f9fc;
}
.adlm-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.adlm-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
}
.adlm-mobile-date__clear {
  border: 0;
  background: transparent;
  color: #e91e63;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
}
.adlm-mobile-date__picker {
  width: 100%;
}
.adlm-mobile-date__picker ::v-deep .vpd-main,
.adlm-mobile-date__picker ::v-deep .vpd-wrapper,
.adlm-mobile-date__picker ::v-deep .vpd-container,
.adlm-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
}
.adlm-confirm-sheet {
  border-radius: 6px 6px 0 0 !important;
  padding: 8px 16px 16px;
  background: #fff !important;
}
.adlm-confirm-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 14px;
  border-radius: 6px;
  background: #cfd8dc;
}
.adlm-confirm-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}
.adlm-confirm-sheet__title {
  text-align: center;
  font-size: 16px;
  font-weight: 800;
  color: #263238;
  margin-bottom: 4px;
}
.adlm-confirm-sheet__subtitle {
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: #78909c;
  margin: 0 0 14px;
}
.adlm-confirm-sheet__rows {
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: #f8fafc;
  padding: 4px 12px;
  margin-bottom: 14px;
}
.adlm-confirm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
  font-size: 13px;
  color: #546e7a;
}
.adlm-confirm-sheet__row:last-child {
  border-bottom: 0;
}
.adlm-confirm-sheet__row strong {
  color: #263238;
  font-weight: 700;
  text-align: left;
}
.adlm-confirm-sheet__items {
  max-height: 160px;
  overflow-y: auto;
  padding: 4px 0 8px;
}
.adlm-confirm-sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px dashed #e0e4e8;
}
.adlm-confirm-sheet__item:last-child {
  border-bottom: 0;
}
.adlm-confirm-sheet__item-main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.adlm-confirm-sheet__item-index {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(91, 124, 153, 0.12);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.adlm-confirm-sheet__item-name {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adlm-confirm-sheet__item-number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.adlm-confirm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.adlm-confirm-sheet__yes {
  border-radius: 6px !important;
}
.adlm-confirm-sheet__no {
  border-radius: 6px !important;
}

/* Accounting-specific additions */
.adlm__count {
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
  padding: 2px 4px 0;
}
.adlm-card__head-side {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.adlm-card__sub--clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}
.adlm-doc-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  color: #b26a00;
  background: rgba(255, 179, 0, 0.14);
}
.adlm-doc-pill--always {
  color: #2e7d32;
  background: rgba(46, 125, 50, 0.12);
}
.adlm-amount,
.adlm-ltr {
  direction: ltr;
  font-variant-numeric: tabular-nums;
}
.adlm-sum {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.08);
}
.adlm-sum--warn {
  background: rgba(255, 179, 0, 0.12);
}
.adlm-sum__col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.adlm-sum__col span {
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
}
.adlm-sum__col strong {
  font-size: 13px;
  color: #263238;
  direction: ltr;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adlm-f-pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
}
.adlm-f-input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
  outline: none;
  direction: ltr;
  text-align: right;
  font-size: 14px;
  color: #37474f;
}
.adlm-f-input:focus {
  border-color: #5b7c99;
}
</style>
